import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { AppLock, type AppLockInstance } from '@ascend/applock';

const DEFERRED_KEY = 'app-lock-deferred';

export type LockDecision = 'pending' | 'skipped' | 'enabled';

const APPLOCK_META_KEYS = [
  'applock_hash',
  'applock_salt',
  'applock_config',
  'applock_version',
];

function clearAppLockMetaKeys(): void {
  for (const key of APPLOCK_META_KEYS) {
    localStorage.removeItem(key);
  }
}

function getStoredDecision(): LockDecision {
  try {
    const stored = localStorage.getItem(DEFERRED_KEY);
    if (stored === 'skipped') return 'skipped';
    if (stored === 'enabled') return 'enabled';
  } catch {
    // localStorage unavailable
  }
  return 'pending';
}

function createLock(): AppLockInstance {
  return AppLock.init({
    mode: 'encrypted',
    storageBackend: 'indexedDB',
    idbDatabaseName: 'amino-pk',
    idbStoreName: 'applock-kv',
    appRootSelector: '#root',
    lockOnHide: true,
    idleTimeout: 300,
    ui: false,
    cleartextKeys: ['amino-pk.settings', 'amino-pk.disclaimer'],
  });
}

interface LockState {
  lock: AppLockInstance | null;
  isLocked: boolean;
  isUnlocked: boolean;
  isConfigured: boolean;
  decision: LockDecision;
}

interface AppLockContextValue extends LockState {
  isEnabled: boolean;
  skipEncryption: () => void;
  enableEncryption: () => void;
}

const DEFAULT_CONTEXT: AppLockContextValue = {
  lock: null,
  isLocked: false,
  isUnlocked: true,
  isConfigured: false,
  isEnabled: false,
  decision: 'skipped',
  skipEncryption: () => {},
  enableEncryption: () => {},
};

const AppLockContext = createContext<AppLockContextValue>(DEFAULT_CONTEXT);

function getInitialState(): LockState {
  const decision = getStoredDecision();
  return {
    lock: null,
    isLocked: false,
    isUnlocked: decision === 'skipped',
    isConfigured: false,
    decision,
  };
}

export function AppLockProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LockState>(getInitialState);
  const instanceRef = useRef<AppLockInstance | null>(null);

  const skipEncryption = useCallback(() => {
    try {
      localStorage.setItem(DEFERRED_KEY, 'skipped');
    } catch {
      // localStorage unavailable
    }
    clearAppLockMetaKeys();
    if (instanceRef.current) {
      instanceRef.current.destroy();
      instanceRef.current = null;
    }
    setState({
      lock: null,
      isLocked: false,
      isUnlocked: true,
      isConfigured: false,
      decision: 'skipped',
    });
  }, []);

  const enableEncryption = useCallback(() => {
    try {
      localStorage.setItem(DEFERRED_KEY, 'enabled');
    } catch {
      // localStorage unavailable
    }
    clearAppLockMetaKeys();
    setState((prev) => ({
      ...prev,
      decision: 'enabled',
      isUnlocked: false,
      isConfigured: false,
    }));
  }, []);

  useEffect(() => {
    if (state.decision !== 'enabled') return;

    const instance = createLock();
    instanceRef.current = instance;

    const onLock = () => {
      setState((prev) => ({ ...prev, isLocked: true, isUnlocked: false }));
    };
    const onUnlock = () => {
      setState((prev) => ({
        ...prev,
        isLocked: false,
        isUnlocked: true,
        isConfigured: true,
      }));
    };
    const onPasswordClear = () => {
      setState((prev) => ({ ...prev, isConfigured: false }));
    };
    const onSetup = () => {
      setState((prev) => ({ ...prev, isConfigured: true }));
    };

    instance.on('lock', onLock);
    instance.on('unlock', onUnlock);
    instance.on('password:clear', onPasswordClear);
    instance.on('setup', onSetup);

    setState((prev) => ({
      ...prev,
      lock: instance,
      isLocked: instance.isLocked,
      isUnlocked:
        instance.state === 'unlocked' ||
        instance.state === 'uninitialized',
      isConfigured: instance.isConfigured,
    }));

    return () => {
      instance.destroy();
      instanceRef.current = null;
    };
  }, [state.decision]);

  if (state.decision === 'enabled' && !state.lock) return null;

  return (
    <AppLockContext.Provider
      value={{
        ...state,
        isEnabled: state.decision === 'enabled' && state.lock !== null,
        skipEncryption,
        enableEncryption,
      }}
    >
      {children}
    </AppLockContext.Provider>
  );
}

export function useAppLock(): AppLockContextValue {
  return useContext(AppLockContext);
}
